/**
 * @fileoverview Field keys, row layout, and file rules for registration documents.
 * @module components/AppointmentRegistrationSections/Documents/documentFieldConfig
 */
import { createFileObjectFromUrl } from "../../../utils/common";
import {
  DOCUMENT_ONLY_MIME_TYPES,
  PDF_AND_IMAGE_MIME_TYPES,
} from "./documentsMimeConstants.js";

export const DOCUMENT_FIELD_KEYS = [
  "brochure",
  "cedulaFiscal",
  "opinionDeCumplimientoPositiva",
  "polizaDeResponsabilidadCivil",
  "altaIMSS",
  "REPSE",
  "comprobanteDeCertificacion",
];

const DOCUMENT_FIELD_ROWS = {
  brochure: 0,
  cedulaFiscal: 0,
  opinionDeCumplimientoPositiva: 0,
  polizaDeResponsabilidadCivil: 1,
  altaIMSS: 1,
  REPSE: 1,
  comprobanteDeCertificacion: 2,
};

const DEFAULT_FILE_TYPE_ERROR_KEY = "components.documents.defaultFileTypeError";
const DEFAULT_FILE_SIZE_ERROR_KEY = "components.documents.defaultFileSizeError";
const TEN_MB = 10 * 1024 * 1024;
const FIFTEEN_MB = 15 * 1024 * 1024;

const pdfOrImageRequirement = (t, maxSize = TEN_MB) => ({
  maxSize,
  allowedTypes: PDF_AND_IMAGE_MIME_TYPES,
  errorMessages: {
    type: t(DEFAULT_FILE_TYPE_ERROR_KEY),
    size: t(DEFAULT_FILE_SIZE_ERROR_KEY),
  },
});

/**
 * Builds per-field size and MIME rules with translated error text.
 * @param {Function} t
 * @returns {Object}
 */
export const buildFileRequirements = (t) => ({
  documentos: {
    maxSize: TEN_MB,
    allowedTypes: DOCUMENT_ONLY_MIME_TYPES,
    errorMessages: {
      type: t("components.documents.documentsFileTypeError"),
      size: t("components.documents.documentsFileSizeError"),
    },
  },
  brochure: {
    ...pdfOrImageRequirement(t, FIFTEEN_MB),
    errorMessages: {
      type: t("components.documents.brochureFileTypeError"),
      size: t("components.documents.brochureFileSizeError"),
    },
  },
  cedulaFiscal: pdfOrImageRequirement(t),
  opinionDeCumplimientoPositiva: pdfOrImageRequirement(t),
  polizaDeResponsabilidadCivil: pdfOrImageRequirement(t),
  altaIMSS: pdfOrImageRequirement(t),
  REPSE: pdfOrImageRequirement(t),
  comprobanteDeCertificacion: pdfOrImageRequirement(t),
});

/**
 * @param {Function} t
 * @returns {Object<string, string>}
 */
export const buildFieldLabels = (t) => ({
  brochure: t("components.documents.brochureLabel"),
  cedulaFiscal: t("components.documents.taxIDLabel"),
  opinionDeCumplimientoPositiva: t(
    "components.documents.positiveComplianceOpinionLabel",
  ),
  polizaDeResponsabilidadCivil: t(
    "components.documents.civilLiabilityPolicyLabel",
  ),
  altaIMSS: t("components.documents.IMSSRegistrationLabel"),
  REPSE: t("components.documents.REPSELabel"),
  comprobanteDeCertificacion: t(
    "components.documents.proofOfCertificationLabel",
  ),
});

export const createInitialFiles = (company) =>
  Object.fromEntries(
    DOCUMENT_FIELD_KEYS.map((key) => [
      key,
      createFileObjectFromUrl(company?.[key]),
    ]),
  );

export const createInitialErrors = () =>
  Object.fromEntries(DOCUMENT_FIELD_KEYS.map((key) => [key, ""]));

/**
 * Groups company-owned document fields into the existing row layout.
 * @param {Object} company
 * @returns {string[][]}
 */
export const groupVisibleDocumentFields = (company) => {
  const visibleFields = DOCUMENT_FIELD_KEYS.filter(
    (fieldKey) => company && Object.hasOwn(company, fieldKey),
  );
  return visibleFields.reduce((rows, fieldKey) => {
    const rowIndex = DOCUMENT_FIELD_ROWS[fieldKey] ?? 0;
    if (!rows[rowIndex]) {
      rows[rowIndex] = [];
    }
    rows[rowIndex].push(fieldKey);
    return rows;
  }, []);
};
